import type {
  DiscountCodeTarget,
  DiscountCodeType,
} from "../models/DiscountCode";

/** Discount applied to a checkout, snapshotted from a DiscountCode at apply time. */
export interface AppliedCheckoutDiscount {
  /** The matched code string (as stored on the DiscountCode). */
  code: string;
  type: DiscountCodeType;
  value: number;
  target: DiscountCodeTarget;
}

/**
 * Computes the discount amount (positive integer cents) to subtract from an
 * order, given the base the discount applies to. Capped at `baseCents` so the
 * order total can never go negative.
 */
export function computeDiscountAmountCents({
  type,
  value,
  baseCents,
}: {
  type: DiscountCodeType;
  value: number;
  baseCents: number;
}): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (!Number.isFinite(baseCents) || baseCents <= 0) return 0;
  let cents: number;
  if (type === "PERCENTAGE") {
    cents = Math.round((baseCents * value) / 100);
  } else {
    // FIXED value is expressed in dollars.
    cents = Math.round(value * 100);
  }
  return Math.max(0, Math.min(cents, Math.round(baseCents)));
}

/** Title used for the discount line item shown in the order summary / snapshot. */
export function discountLineTitle(code: string | undefined): string {
  const c = code?.trim();
  return c ? `Discount (${c})` : "Discount";
}
